import { useContext, useEffect } from 'react'

import {
  DataLayerEventType,
  DataLayerFactory,
  isTmsReady,
  TmsContext,
} from '@awazevr/mfe-common.contexts.tms'
import { OptimizelyDecisionWithOutUserContext } from '@awazevr/mfe-common.lib.optimizely'

import { OPTIMIZELY_EVENTS } from '@/constants'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

interface UseSearchPageLoadTrackingProps {
  experimentDecisions: Record<string, OptimizelyDecisionWithOutUserContext>
  optimizelyUserId: string
}

// Shared by the traditional and beta search layouts.
export const useSearchPageLoadTracking = ({
  experimentDecisions,
  optimizelyUserId,
}: UseSearchPageLoadTrackingProps): void => {
  const tms = useContext(TmsContext)
  const optiContext = useOptimizelyDecisionsContext()
  const tmsReady = isTmsReady(tms)

  useEffect(() => {
    const shouldTriggerDataLayerPush =
      tmsReady && Object.keys(experimentDecisions).length > 0

    typeof newrelic !== 'undefined' &&
      tmsReady &&
      Object.keys(experimentDecisions).length === 0 &&
      newrelic.addPageAction('Optimizely-LogDecisions-NoDecisions', {
        userId: optimizelyUserId,
      })

    if (shouldTriggerDataLayerPush) {
      typeof newrelic !== 'undefined' &&
        newrelic.addPageAction('Optimizely-LogDecisions-Trigger', {
          userId: optimizelyUserId,
        })

      Object.keys(experimentDecisions).forEach((experimentDecision) => {
        new DataLayerFactory()
          .getEvent(DataLayerEventType.ExperienceImpression)
          .update(
            tms,
            {
              experiment_origin: 'flag',
              experiment_decision: experimentDecisions[experimentDecision],
            },
            dataLayerErrorHandler,
          )
      })
    }
  }, [tmsReady])

  // Once per page load, not per results update.
  useEffect(() => {
    void trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.PAGE_VIEW,
      optiContext,
    )
  }, [])
}
